import _extends from "@babel/runtime/helpers/extends";
import { loadNewForm, metadataSelector, updateConfig } from "@icg360/rex";
import store from "../store";
import getProductForms from "./getProductForms";
import getSupplementalPolicyTerms from "./getSupplementalPolicyTerms";
export function readProductForms(productFormsUnknown, policyActionId) {
  // Make sure the particular form that's been requested exists in the
  // `productForms` we fetched. If it doesn't, don't bother loading anything
  // into rex as we're not going to need to.
  if (Array.isArray(productFormsUnknown) && productFormsUnknown.find(function (form) {
    return form.name === policyActionId;
  }) || productFormsUnknown.type === "product-form" && productFormsUnknown.name === policyActionId) {
    return {
      exists: true,
      forms: productFormsUnknown
    };
  }

  if (productFormsUnknown.type === "product-form-bundle") {
    return {
      exists: true,
      forms: productFormsUnknown.forms[policyActionId]
    };
  }

  return {
    exists: false,
    forms: []
  };
}
/**
 * Get all the data necessary for the form to render.
 *   - Fetch the `productId` for the policy, which we'll use to
 *   - Fetch the `productForms` which we'll feed into rex
 *   - Fetch the `supplementalPolicyData` based on the metadata from the
 *     `productForms` we fed into rex.
 *
 * @param {String} apiRulesetsUrl
 * @param {String} apiIxvocabUrl
 * @param {String} apiIxvocabAuth
 * @param {String} apiQuotingUrl
 * @param {String} applicationId
 * @param {string} pacId
 * @param {string} policyActionId
 * @param {Object} policyData
 * @param {String} productId
 *
 * @return {Promise<Object>}
 *         {boolean} Promise<Object.productFormExists>
 *                   Whether the `policyActionId` exists in `productForms` we tried to fetch.
 *         {string}  Promise<Object.suppelmentalPolicyData>
 *                   The data from ixvocab (if relevant)
 */

export default function getPacData(_ref) {
  var apiQuotingUrl = _ref.apiQuotingUrl,
      apiRulesetsUrl = _ref.apiRulesetsUrl,
      applicationId = _ref.applicationId,
      apiIxvocabAuth = _ref.apiIxvocabAuth,
      apiIxvocabUrl = _ref.apiIxvocabUrl,
      pacId = _ref.pacId,
      policyActionId = _ref.policyActionId,
      policyData = _ref.policyData,
      productId = _ref.productId;
  var productFormExists = false;
  var rexFormMetadata = {};
  return getProductForms({
    apiQuotingUrl: apiQuotingUrl,
    productId: productId
  }).then(function (productFormsUnknown) {
    return readProductForms(productFormsUnknown, policyActionId);
  }).then(function (_ref2) {
    var exists = _ref2.exists,
        forms = _ref2.forms;
    productFormExists = exists; // If the product form exists, we'll load it into rex and get back the
    // metadata for that form. Otherwise, return `undefined` so we know not to
    // try fetching anything extra.

    rexFormMetadata = loadProductFormsIntoRex({
      apiRulesetsUrl: apiRulesetsUrl,
      applicationId: applicationId,
      pacId: pacId,
      policyActionId: policyActionId,
      policyData: policyData,
      productForms: forms
    });
    return rexFormMetadata.ixVocabContext ? getSupplementalPolicyTerms({
      apiIxvocabAuth: apiIxvocabAuth,
      apiIxvocabUrl: apiIxvocabUrl,
      ixVocabContextId: rexFormMetadata.ixVocabContext
    }) : undefined;
  }).then(function (supplementalPolicyTerms) {
    return {
      rexFormMetadata: rexFormMetadata,
      supplementalPolicyTerms: supplementalPolicyTerms,
      productFormExists: productFormExists
    };
  });
}
/**
 * Initialize Rex with the dynamic `productForms` data we fetched.
 * Then query rex to find the metadata associated with the dynamic form and return it
 *
 * @param {String} apiRulesetsUrl
 * @param {String} applicationId
 * @param {String} pacId
 * @param {String} policyActionId
 * @param {Object} policyData
 * @param {Object[]} productForms
 * @return {Promise<Object|undefined>}
 */

function loadProductFormsIntoRex(_ref3) {
  var apiRulesetsUrl = _ref3.apiRulesetsUrl,
      applicationId = _ref3.applicationId,
      pacId = _ref3.pacId,
      policyActionId = _ref3.policyActionId,
      policyData = _ref3.policyData,
      productForms = _ref3.productForms;
  // Add ixlogic to rex's config so rulesets work.
  // Note: we add a trailing slash to the value as it'll break otherwise
  // (all the api url props for <Pac /> get their trailing slashes stripped)
  // Note 2: once the product team updates all postbind products, we can remove
  // the dependency of having a trailing slash.
  store.dispatch(updateConfig({
    ixlogic: apiRulesetsUrl + "/"
  })); // Load a new form into Rex

  store.dispatch(loadNewForm({
    forms: productForms,
    id: pacId,
    initialData: _extends({}, policyData, {
      // Environment-specific information which can be referenced in the binds
      // of the product for running logic
      ENV_application: applicationId
    }),
    childForm: policyActionId
  })); // Now that we loaded a form into rex, we need to query that form's metadata
  // from Rex and we'll pass that down to the <Pac /> instance.
  // That data is set by the product implementation team and allows dynamic
  // control of some of the things happening inside <Pac />

  var state = store.getState();
  var metadata = metadataSelector(state, {
    useForm: {
      id: pacId,
      childId: policyActionId
    }
  });
  return metadata;
}